import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Save } from "lucide-react";

const SLOTS = ["backup_horario_1", "backup_horario_2", "backup_horario_3"];
const fromEmpresa = (e) => Object.fromEntries(SLOTS.map((k) => [k, e[k] || ""]));

// Até 3 horários próprios por empresa (sobrescrevem o horário global). Vazio = fallback global.
export function EmpresaSchedulesCard({ empresas }) {
  const [schedules, setSchedules] = useState({});
  const [savingId, setSavingId] = useState(null);
  useEffect(() => {
    setSchedules(Object.fromEntries(empresas.map((e) => [e.id, fromEmpresa(e)])));
  }, [empresas]);

  const save = async (empresaId) => {
    setSavingId(empresaId);
    try {
      await api.put(`/empresas/${empresaId}`, schedules[empresaId] || fromEmpresa({}));
      toast.success("Horários salvos — aplicado no próximo ciclo (~1min)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar horários da empresa");
    } finally { setSavingId(null); }
  };

  return (
    <Card data-testid="card-empresas-schedules">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="w-4 h-4 text-sky-600" />
          Horários por Empresa (até 3× por dia · sobrescreve o global)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">
          Cada empresa pode ter até 3 horários próprios de backup automático. Se todos estiverem vazios, cai no horário global acima. Aplicado em até ~1 minuto pelo scheduler.
        </p>
        <div className="max-h-[420px] overflow-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold">Empresa</th>
                <th className="px-3 py-2 font-semibold w-[110px]">Backup 1</th>
                <th className="px-3 py-2 font-semibold w-[110px]">Backup 2</th>
                <th className="px-3 py-2 font-semibold w-[110px]">Backup 3</th>
                <th className="px-3 py-2 font-semibold w-[100px]"></th>
              </tr>
            </thead>
            <tbody>
              {empresas.map((e) => (
                <ScheduleRow key={e.id} empresa={e} value={schedules[e.id] || fromEmpresa(e)}
                  saving={savingId === e.id}
                  onChange={(next) => setSchedules((s) => ({ ...s, [e.id]: next }))}
                  onSave={() => save(e.id)} />
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function ScheduleRow({ empresa: e, value, saving, onChange, onSave }) {
  const original = fromEmpresa(e);
  const dirty = SLOTS.some((k) => value[k] !== original[k]);
  return (
    <tr className="border-t hover:bg-slate-50/60" data-testid={`sched-row-${e.id}`}>
      <td className="px-3 py-1.5 font-medium">
        {e.nome} <span className="text-[10px] text-muted-foreground font-normal">({e.slug || e.id.slice(0, 6)})</span>
      </td>
      {SLOTS.map((k, i) => (
        <td key={k} className="px-2 py-1.5">
          <input type="time" value={value[k] || ""} onChange={(ev) => onChange({ ...value, [k]: ev.target.value })}
            data-testid={`sched-input-${e.id}-${i + 1}`} className="border rounded px-2 py-1 font-mono text-xs w-full" />
        </td>
      ))}
      <td className="px-2 py-1.5">
        <Button size="sm" variant={dirty ? "default" : "outline"} disabled={!dirty || saving} onClick={onSave}
          data-testid={`btn-save-sched-${e.id}`} className={dirty ? "bg-sky-600 hover:bg-sky-700 h-8" : "h-8"}>
          <Save className={`w-3.5 h-3.5 mr-1 ${saving ? "animate-pulse" : ""}`} />
          {saving ? "…" : "Salvar"}
        </Button>
      </td>
    </tr>
  );
}
